import { AnimatePresence } from "framer-motion";
import React, { useState } from "react";
import { UserRole, User as UserType } from "../../types/user";
import AddressForm from "./forms/AddressForm";
import BasicInfoForm from "./forms/BasicInfoForm";
import InstitutionForm from "./forms/InstitutionForm";
import RoleSelection from "./forms/RoleSelection";
import RoleSpecificFields from "./forms/RoleSpecificFields";
import { useUserFormData } from "./hooks/useUserFormData";

interface DynamicUserFormProps {
  editingUser?: UserType | null;
  onSave: (userData: any) => void;
  onCancel: () => void;
}

const DynamicUserForm: React.FC<DynamicUserFormProps> = ({
  editingUser,
  onSave,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState<UserRole>(
    editingUser?.role || ""
  );
  const [loading, setLoading] = useState(false);
  const [showNewInstitutionForm, setShowNewInstitutionForm] = useState(false);
  const [showNewBranchForm, setShowNewBranchForm] = useState(false);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);

  const {
    formData,
    addressData,
    institutionData,
    handleAddressChange,
    handleInstitutionChange,
    handleInputChange,
    handleCreateInstitution,
    handleCreateBranch,
    handleCreateServiceCategory,
    handleSubmit,
  } = useUserFormData(editingUser);

  const roleCategories = {
    admin: ["admin_system", "admin_super"],
    staff: ["staff_support", "staff_delivery", "staff_manager", "staff_hr"],
    service: ["stylist", "receptionist"],
    customer: ["customer_basic", "customer_premium", "customer_vip", "client"],
    provider: [
      "provider_individual",
      "provider_business",
      "provider_enterprise",
    ],
    stakeholder: ["stakeholder_read"],
  };

  // Roles that require institution and branch selection
  const institutionRequiredRoles = [
    "admin_system",
    "staff_support",
    "staff_delivery",
    "staff_manager",
    "staff_hr",
    "stylist",
    "receptionist",
  ];

  const getRoleDisplayName = (role: UserRole) => {
    return role.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getRoleCategory = (role: UserRole) => {
    for (const [category, roles] of Object.entries(roleCategories)) {
      if (roles.includes(role)) return category;
    }
    return "other";
  };

  const requiresInstitution = (role: UserRole) => {
    return institutionRequiredRoles.includes(role);
  };

  const handleRoleChange = (role: UserRole) => {
    setSelectedRole(role);
    handleInputChange("role", role);
    setCurrentStep(2);
  };

  const onCreateInstitution = async (formData: FormData) => {
    const success = await handleCreateInstitution(formData);
    if (success) {
      setShowNewInstitutionForm(false);
    }
  };

  const onCreateBranch = async (formData: FormData) => {
    const success = await handleCreateBranch(formData);
    if (success) {
      setShowNewBranchForm(false);
    }
  };

  const onCreateServiceCategory = async (formData: FormData) => {
    const success = await handleCreateServiceCategory(formData);
    if (success) {
      setShowNewCategoryForm(false);
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    const result = await handleSubmit(selectedRole);
    if (result.success) {
      onSave(result.data);
    }
    setLoading(false);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selectedRole !== "";
      case 2:
        return formData.name && formData.email && formData.phone;
      case 3:
        if (requiresInstitution(selectedRole)) {
          return formData.institutionId && formData.branchId;
        }
        return formData.region && formData.district && formData.county;
      case 4:
        return formData.region && formData.district && formData.county;
      case 5:
        return true; // Role-specific fields are optional for now
      default:
        return false;
    }
  };

  const totalSteps = requiresInstitution(selectedRole) ? 5 : 4;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              {editingUser ? "Edit User" : "Create New User"}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-500"
            >
              ×
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
              <span>
                Step {currentStep} of {totalSteps}
              </span>
              <span>
                {Math.round((currentStep / totalSteps) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <RoleSelection
                onRoleSelect={handleRoleChange}
                roleCategories={roleCategories}
                requiresInstitution={requiresInstitution}
                getRoleDisplayName={getRoleDisplayName}
              />
            )}
            {currentStep === 2 && (
              <BasicInfoForm
                formData={formData}
                selectedRole={selectedRole}
                onInputChange={handleInputChange}
                getRoleDisplayName={getRoleDisplayName}
              />
            )}
            {currentStep === 3 && requiresInstitution(selectedRole) && (
              <InstitutionForm
                formData={formData}
                selectedRole={selectedRole}
                institutionData={institutionData}
                showNewInstitutionForm={showNewInstitutionForm}
                showNewBranchForm={showNewBranchForm}
                showNewCategoryForm={showNewCategoryForm}
                onInstitutionChange={handleInstitutionChange}
                onInputChange={handleInputChange}
                setShowNewInstitutionForm={setShowNewInstitutionForm}
                setShowNewBranchForm={setShowNewBranchForm}
                setShowNewCategoryForm={setShowNewCategoryForm}
                onCreateInstitution={onCreateInstitution}
                onCreateBranch={onCreateBranch}
                onCreateServiceCategory={onCreateServiceCategory}
              />
            )}
            {currentStep === 3 && !requiresInstitution(selectedRole) && (
              <AddressForm
                formData={formData}
                addressData={addressData}
                onAddressChange={handleAddressChange}
                onInputChange={handleInputChange}
              />
            )}
            {currentStep === 4 && requiresInstitution(selectedRole) && (
              <AddressForm
                formData={formData}
                addressData={addressData}
                onAddressChange={handleAddressChange}
                onInputChange={handleInputChange}
              />
            )}
            {currentStep === 4 && !requiresInstitution(selectedRole) && (
              <RoleSpecificFields
                formData={formData}
                selectedRole={selectedRole}
                onInputChange={handleInputChange}
                getRoleDisplayName={getRoleDisplayName}
                getRoleCategory={getRoleCategory}
              />
            )}
            {currentStep === 5 && (
              <RoleSpecificFields
                formData={formData}
                selectedRole={selectedRole}
                onInputChange={handleInputChange}
                getRoleDisplayName={getRoleDisplayName}
                getRoleCategory={getRoleCategory}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={() => {
              if (currentStep === 1) {
                onCancel();
              } else {
                setCurrentStep((prev) => prev - 1);
              }
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-md"
          >
            {currentStep === 1 ? "Cancel" : "Back"}
          </button>

          <button
            onClick={() => {
              if (currentStep === totalSteps) {
                onSubmit();
              } else {
                setCurrentStep((prev) => prev + 1);
              }
            }}
            disabled={!canProceed() || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Saving..."
              : currentStep === totalSteps
              ? editingUser
                ? "Update User"
                : "Create User"
              : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DynamicUserForm;
